import { motion, AnimatePresence } from 'framer-motion';
import { PhoneOffIcon } from './icons/PhoneOffIcon';

interface EndCallConfirmationProps {
  isVisible: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Modal confirmation dialog for ending a consultation.
 *
 * Prevents accidental call termination by requiring explicit confirmation.
 * Uses the same visual pattern as DegradationPrompt for consistency.
 */
export function EndCallConfirmation({
  isVisible,
  onConfirm,
  onCancel,
}: EndCallConfirmationProps) {
  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
          data-testid="end-call-confirmation"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-2xl max-w-md w-full p-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="end-call-title"
          >
            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <PhoneOffIcon size={24} className="text-red-500" />
              </div>
            </div>

            <h2
              id="end-call-title"
              className="text-xl font-serif text-brand-navy text-center mb-2"
            >
              End Consultation?
            </h2>

            <p className="text-gray-600 text-center mb-6 text-sm">
              Are you sure you want to end this consultation? This action cannot be undone.
            </p>

            <div className="flex flex-col gap-3">
              <button
                onClick={onConfirm}
                className="w-full py-3 bg-red-500 hover:bg-red-600 text-white font-medium rounded-lg transition-colors"
              >
                End Consultation
              </button>

              <button
                onClick={onCancel}
                className="w-full py-3 border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
